import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import PageCardAnalytics from "@v2/features/home/components/PageCard/PageCardAnalytics";
import usePublishedSiteUrls from "@v2/features/home/hooks/usePublishedSiteUrls";
import useShopDomain from "@v2/features/home/hooks/useShopDomain";
import { DeploymentStatusTag } from "@v2/features/page-renderer/components/DeploymentStatusTag";
import { DeletePageModal } from "@v2/features/page-renderer/components/navbar/DeletePageModal";
import { EditablePageName } from "@v2/features/page-renderer/components/navbar/EditablePageName";
import { Link } from "react-router-dom";

import { DevicePreview } from "../DevicePreview";
import PageCardMenu from "./PageCardMenu";

const CARD_CONSTANTS = {
  PREVIEW_HEIGHT: 155,
  MOBILE_MAX_WIDTH: 140,
  MIN_CARD_WIDTH: 300,
} as const;

const PageCard = ({
  file,
  layoutClassName,
}: {
  file: FileWithLatestFileVersion;
  layoutClassName?: string;
}) => {
  const { project } = useCurrentProjectContext();

  const [isRenamingPage, setIsRenamingPage] = React.useState(false);
  const [deletePageModalOpen, setDeletePageModalOpen] = React.useState(false);

  const isPublished = Boolean(file.lastPublishedFileVersionId);

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: [file.pageRoute ?? ""],
  });

  const publishedSiteUrl = publishedSiteUrls[0];
  const shopDomain = useShopDomain();
  const isMobile = useIsMobile();

  if (!project) {
    return null;
  }

  return (
    <>
      <Link
        to={`/shops/${project.id}/chat/?pageId=${file.id}`}
        className="h-full group cursor-pointer"
      >
        <div
          className={twMerge(
            "border-0.5 border-border rounded-lg bg-sheet group-hover:bg-menu-item-hover flex flex-col cursor-pointer shadow-sm transition-all duration-200 h-full group-hover:scale-[0.99] group-hover:shadow-lg",
            layoutClassName,
          )}
          style={{
            minWidth: isMobile
              ? CARD_CONSTANTS.MOBILE_MAX_WIDTH
              : CARD_CONSTANTS.MIN_CARD_WIDTH,
          }}
        >
          <div className="p-4 pb-0 flex flex-col gap-2">
            <div
              className="flex justify-center items-start gap-4 overflow-hidden"
              style={{ height: `${CARD_CONSTANTS.PREVIEW_HEIGHT}px` }}
            >
              <DevicePreview
                pageRoute={file.pageRoute ?? ""}
                className="w-full h-full pointer-events-none"
                isScrollable={false}
                hideScrollbars={true}
                url={publishedSiteUrl ?? shopDomain}
                mobileClassName="pointer-events-none h-full w-full"
                maxMobileWidth={CARD_CONSTANTS.MOBILE_MAX_WIDTH}
                desktopClassName="pointer-events-none h-full w-full border-l-[1px] border-r-[1px] border-t-[1px] border-border/25 rounded-t-md"
                cropToFit
              />
            </div>
          </div>

          <div className="px-4 py-3 flex flex-col gap-2 border-t-0.5 border-border bg-white rounded-b-lg">
            <div className="flex justify-between items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {isRenamingPage ? (
                  <EditablePageName
                    page={file}
                    projectId={project.id}
                    setIsRenaming={setIsRenamingPage}
                    size="sm"
                  />
                ) : (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsRenamingPage(true);
                    }}
                    className="header-h6 text-default truncate min-w-0"
                    title={file.name}
                  >
                    {file.name}
                  </div>
                )}

                <DeploymentStatusTag file={file} />
              </div>

              <div className="shrink-0">
                <PageCardMenu
                  page={file}
                  isPublished={isPublished}
                  publishedSiteUrl={publishedSiteUrl}
                  setIsRenamingPage={setIsRenamingPage}
                  onDeleteClick={() => setDeletePageModalOpen(true)}
                />
              </div>
            </div>

            <PageCardAnalytics
              file={file}
              publishedSiteUrl={publishedSiteUrl}
            />
          </div>
        </div>
      </Link>
      <DeletePageModal
        open={deletePageModalOpen}
        onOpenChange={setDeletePageModalOpen}
        page={file}
      />
    </>
  );
};

export default PageCard;
